import type { Href } from 'expo-router';
import { useFocusEffect } from 'expo-router';
import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    type GetProps,
    GRADIENT_COLOR_TOP,
    isNonMobileWeb,
    isWeb,
    ScrollView,
    View,
    YStack,
} from 'smoothie';

import ProgressiveBlurHeader from '../../common/components/ProgressiveBlurHeader';
import OnboardingHeader from './OnboardingHeader';
interface OnboardingFullScreenViewProps {
    scrollable?: boolean;
    hasHeader?: boolean;
    children: React.ReactNode;
    hasBackButton?: boolean;
    backRoute?: Href;
    scrollViewProps?: GetProps<typeof ScrollView>;
    /** Enable mobile Safari full-bleed overrides (document scrolling, Liquid
     *  Glass bar tinting). Only use on standalone fullscreen onboarding pages
     *  — NOT when embedded inside a tabbed/nav layout. */
    webFullScreen?: boolean;
    /** Set Safari's Liquid Glass bar tint to match the onboarding background
     *  without changing layout. Use on pages that have their own ScrollView
     *  but still need matching bar colors. */
    webSafariBarTint?: boolean;
}

const OnboardingFullScreenView = ({
    scrollable = true,
    hasHeader = true,
    children,
    hasBackButton = false,
    backRoute,
    scrollViewProps,
    webFullScreen = false,
    webSafariBarTint = false,
}: OnboardingFullScreenViewProps) => {
    // On mobile Safari, Expo Router / React Navigation wraps every screen in
    // deeply nested divs with inline height + overflow constraints. A <style>
    // tag with !important overrides ALL of them so the document can scroll
    // natively (enables URL bar collapse + Liquid Glass bar tinting).
    // Uses useFocusEffect so the overrides are removed when another screen is
    // pushed on top (the start page stays mounted in the Stack, and the CSS
    // would otherwise break the new screen's layout via shared ancestors).
    useFocusEffect(
        useCallback(() => {
            if (Platform.OS !== 'web' || !webFullScreen) return;

            const style = document.createElement('style');
            style.id = 'onboarding-fullscreen';
            style.textContent = `
                @media (max-width: 768px) {
                    html, body, #root,
                    #root div:has([data-onboarding]) {
                        height: auto !important;
                        min-height: 100% !important;
                        overflow: visible !important;
                        position: relative !important;
                        inset: unset !important;
                    }
                    body {
                        background-color: #DAEDE4 !important;
                    }
                }
            `;
            document.head.appendChild(style);

            return () => {
                style.remove();
            };
        }, [webFullScreen]),
    );

    // Lighter alternative: only set body background-color for Safari's bar
    // tinting without changing layout. Safe for pages with modals/sheets.
    useFocusEffect(
        useCallback(() => {
            if (Platform.OS !== 'web' || !webSafariBarTint || webFullScreen)
                return;

            document.body.style.backgroundColor = '#DAEDE4';

            return () => {
                document.body.style.backgroundColor = '';
            };
        }, [webSafariBarTint, webFullScreen]),
    );

    return (
        <View
            {...(webFullScreen && isWeb ? { 'data-onboarding': '' } : {})}
            height={webFullScreen && isWeb ? undefined : '100%'}
            minHeight={
                webFullScreen && isWeb
                    ? ('100dvh' as unknown as number)
                    : undefined
            }
            width="100%"
            backgroundColor="#DAEDE4"
        >
            {scrollable && !(webFullScreen && isWeb) && (
                <ProgressiveBlurHeader
                    pageBackgroundColor={GRADIENT_COLOR_TOP}
                />
            )}
            <ContentWrapper
                scrollable={scrollable}
                scrollViewProps={scrollViewProps}
                webFullScreen={webFullScreen}
            >
                <ContentContainer
                    hasHeader={hasHeader}
                    hasBackButton={hasBackButton}
                    backRoute={backRoute}
                >
                    {children}
                </ContentContainer>
            </ContentWrapper>
        </View>
    );
};

const ContentWrapper = ({
    scrollable = true,
    children,
    scrollViewProps,
    webFullScreen = false,
}: OnboardingFullScreenViewProps) => {
    // When webFullScreen, skip ScrollView on web — let the document scroll
    // natively. Safari only collapses the URL bar for document-level scrolling.
    if (webFullScreen && isWeb) return <>{children}</>;

    return scrollable ? (
        <ScrollView
            height="100%"
            width="100%"
            showsVerticalScrollIndicator={isNonMobileWeb}
            contentContainerStyle={{ flexGrow: 1 }}
            scrollEventThrottle={16}
            keyboardShouldPersistTaps="handled"
            {...scrollViewProps}
        >
            {children}
        </ScrollView>
    ) : (
        <>{children}</>
    );
};

const ContentContainer = ({
    hasHeader = true,
    children,
    hasBackButton,
    backRoute,
}: OnboardingFullScreenViewProps) => {
    const insets = useSafeAreaInsets();

    return (
        <YStack
            flex={1}
            height="100%"
            width="100%"
            paddingTop={insets.top + 16}
            paddingHorizontal={24}
            gap={16}
            $mobile_mini={{ paddingHorizontal: 16 }}
            $sm={{ paddingHorizontal: 32 }}
        >
            {hasHeader && (
                <OnboardingHeader
                    hasBackButton={hasBackButton}
                    backRoute={backRoute}
                />
            )}
            <View flex={1}>{children}</View>
        </YStack>
    );
};

export default OnboardingFullScreenView;
